import { query, where, getDocs, getDoc } from 'firebase/firestore';
import { db, getTenantCollection, getTenantDoc } from '../../../firebase/config';
import { logger } from '../../../services/logger';

export async function fetchAccountData(currentUser) {
    let accData = null;
    try {
        const directSnap = await getDoc(getTenantDoc(db, 'accounts', currentUser.uid));
        if (directSnap.exists()) {
            accData = directSnap.data();
        }
    } catch (dErr) {
        logger.warn("Direct account lookup skipped in Navbar:", dErr, { uid: currentUser.uid });
    }

    // If standalone account doc not found or is not yet a community member, check family accounts
    const isSpecialRole = accData?.role === 'admin' || accData?.role === 'coach';
    if (!accData || (accData.isCommunityMember !== 'Yes' && !isSpecialRole)) {
        try {
            const accountsRef = getTenantCollection(db, 'accounts');
            const q = query(accountsRef, where('members', 'array-contains', currentUser.uid));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const communityAcc = snap.docs.find(d => d.data().isCommunityMember === 'Yes') ||
                                     snap.docs.find(d => d.id !== currentUser.uid) ||
                                     snap.docs[0];
                accData = communityAcc.data();
            }
        } catch (qErr) {
            logger.warn("Accounts query fallback skipped in Navbar:", qErr, { uid: currentUser.uid });
        }
    }

    return accData;
}

function collectDocsIntoMap(snap, map) {
    const docs = snap?.forEach ? snap : (snap?.docs || []);
    docs.forEach(d => map.set(d.id, d));
}

async function fetchPersonalRegistrations(uid, regDocsMap) {
    try {
        const fallbackQuery = query(
            getTenantCollection(db, 'registrations'),
            where('accountId', '==', uid)
        );
        const fallbackSnap = await getDocs(fallbackQuery);
        collectDocsIntoMap(fallbackSnap, regDocsMap);
    } catch (fbErr) {
        logger.warn("Navbar: Registrations query failed:", fbErr);
    }
}

export async function fetchLinkedRegistrations(currentUser, accData) {
    const linkedAccountIds = Array.from(new Set([
        currentUser.uid,
        accData?.id,
        ...(accData?.linkedAccountIds || []),
        ...(accData?.members || [])
    ].filter(Boolean)));

    const regDocsMap = new Map();

    if (linkedAccountIds.length > 1) {
        try {
            const regQuery = query(
                getTenantCollection(db, 'registrations'),
                where('accountId', 'in', linkedAccountIds.slice(0, 10))
            );
            const regSnap = await getDocs(regQuery);
            collectDocsIntoMap(regSnap, regDocsMap);
            return regDocsMap;
        } catch (crossErr) {
            logger.warn("Navbar: Cross-account registrations query failed, falling back to personal account:", crossErr);
        }
    }

    await fetchPersonalRegistrations(currentUser.uid, regDocsMap);
    return regDocsMap;
}

export function extractActiveProgramIds(regDocsMap) {
    const activeIds = new Set();
    for (const rDoc of regDocsMap.values()) {
        const reg = typeof rDoc.data === 'function' ? rDoc.data() : rDoc;
        if (reg.status !== 'cancelled' && reg.status !== 'inactive' && reg.programId) {
            activeIds.add(reg.programId);
        }
    }
    return activeIds;
}
